import Box from "@/elements/Box";
import Typography from "@/elements/Typography";
import Loading from "../Loading";
import LoadingError from "../LoadingError";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "@/elements/Table";
import { useAuth } from "@/context/useAuth";
import Button from "@/elements/Button";
import { usePosizioneFilterWithNavigation } from "@/hooks/usePosizioneFilterWithNavigation";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Icon,
  TablePagination,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClienteDialog from "../ClienteDialog";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useConfirmationDialog } from "@/utils/useConfirmationDialog";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import {
  ModelloDispositivoDto,
  getGetModelliDispositivoQueryKey,
  useDeleteModelloDispositivo,
  useGetModelliDispositivo,
} from "@/api";
import ModelloDispositivoDialog from "../ModelloDispositivoDialog";
import { AxiosError } from "axios";

const columnHelper = createColumnHelper<ModelloDispositivoDto>();

function ModelliDispositivoPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { isLoading, error, data } = useGetModelliDispositivo();

  const {
    filters,
    changeFilter,
    filtersLoading,
    page,
    pageSize,
    setPage,
    setPageSize,
  } = usePosizioneFilterWithNavigation("/modelli_dispositivi", {
    enablePagination: true,
    enableTextFilter: true,
  });

  const [modelliDialogState, setModelliDialogState] = useState({
    open: false,
    modello: null as ModelloDispositivoDto | null,
  });

  const [requestError, setRequestError] = useState<AxiosError | null>(null);

  const { askConfirmation, confirmationDialog } = useConfirmationDialog();

  const { mutateAsync } = useDeleteModelloDispositivo({
    mutation: {
      onSuccess: () => {
        toast.success(t("modelliDispositivoPage.toast.eliminato"));
      },
      onError: () => {
        toast.error(t("errori.richiesta"));
      },
    },
  });

  const queryClient = useQueryClient();

  const deleteModello = useCallback(
    async (modelloId: number) => {
      try {
        await mutateAsync({ id: modelloId });
        queryClient.invalidateQueries({
          queryKey: getGetModelliDispositivoQueryKey(),
        });
        // queryClient.invalidateQueries({ queryKey: getGetClientiQueryKey() });
      } catch (err) {
        const axiosError = err as AxiosError;
        if (axiosError) {
          setRequestError(axiosError);
        }
      }
    },
    [mutateAsync, queryClient]
  );

  const columns = useMemo(() => {
    const columns = [
      columnHelper.accessor("imageUrl", {
        header: t("modelliDispositivoPage.colonne.immagine"),
        size: undefined,
        cell: ({ row }) => {
          return (
            row.original.imageUrl && (
              <img
                src={row.original.imageUrl}
                alt={row.original.image?.fileName}
                style={{ width: 100 }}
              />
            )
          );
        },
      }),
      columnHelper.accessor("modello", {
        header: t("modelliDispositivoPage.colonne.modello"),
        size: undefined,
      }),
      columnHelper.accessor("maschera", {
        header: t("modelliDispositivoPage.colonne.maschera"),
        size: undefined,
      }),
      columnHelper.accessor("tipoDispositivo", {
        header: t("modelliDispositivoPage.colonne.tipo"),
        size: undefined,
      }),
      columnHelper.accessor("mappaturaAllarmi.type", {
        header: t("modelliDispositivoPage.colonne.mappatura"),
        size: undefined,
      }),
      hasPermission("ModelliDispositivoChange") &&
        columnHelper.display({
          id: "buttons",
          header: "",
          size: 250,
          maxSize: 250,
          cell: ({ row }) => {
            return (
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  startIcon={<Icon>edit</Icon>}
                  onClick={() => {
                    setModelliDialogState({
                      open: true,
                      modello: row.original,
                    });
                  }}
                >
                  {t("azioni.modifica")}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  startIcon={<Icon>delete_outlined</Icon>}
                  onClick={() => {
                    askConfirmation({
                      title: t("modelliDispositivoPage.confermaElimina.titolo"),
                      message: t("modelliDispositivoPage.confermaElimina.messaggio", {
                        modello: row.original.modello,
                      }),
                      okColor: "error",
                      onConfirm: () => {
                        return deleteModello(row.original.id);
                      },
                    });
                  }}
                >
                  {t("azioni.elimina")}
                </Button>
              </Box>
            );
          },
        }),
    ].filter((x) => x);
    return columns as unknown as Exclude<(typeof columns)[number], boolean>[];
  }, [setModelliDialogState, askConfirmation, deleteModello, hasPermission, t]);

  const allModelli = data?.data || [];

  const modelliFiltrati = useMemo(() => {
    if (!filters.textFilter) {
      return allModelli;
    }

    const filtro = filters.textFilter.toLowerCase();
    return allModelli.filter(
      (modello) =>
        modello.modello?.toLowerCase().includes(filtro) ||
        modello.maschera?.toLowerCase().includes(filtro) ||
        modello.tipoDispositivo?.toLowerCase().includes(filtro)
    );
  }, [allModelli, filters.textFilter]);

  const modelliPagedData = useMemo(() => {
    const startIndex = page * pageSize;
    const endIndex = startIndex + pageSize;
    return modelliFiltrati.slice(startIndex, endIndex);
  }, [modelliFiltrati, page, pageSize]);

  if (isLoading) {
    return <Loading />;
  }
  if (error) {
    return <LoadingError error={error} />;
  }

  return (
    <div>
      <Box p={2}>
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h4" mr={2}>
            {t("modelliDispositivoPage.titolo")}
          </Typography>
          <Box display="flex" gap={1} pt={1} flexWrap="wrap">
            <TextField
              placeholder={t("modelliDispositivoPage.filtra")}
              value={filters.textFilter || ""}
              onChange={(e) => changeFilter("textFilter")(null, e.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ ml: 1.5 }} /> }}
            />
            {hasPermission("UsersEsterniChange") && (
              <Button
                size="small"
                color="light"
                onClick={() => {
                  setModelliDialogState({
                    open: true,
                    modello: null,
                  });
                }}
              >
                {t("modelliDispositivoPage.azioni.aggiungi")}
              </Button>
            )}
          </Box>
        </Box>

        <Table
          columns={columns}
          data={modelliPagedData}
          sortable={true}
          paginated={false}
          initialSort={[{ id: "modello", desc: false }]}
        />

        {modelliFiltrati.length > 0 && (
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="flex-end"
            pt={2}
          >
            <TablePagination
              component="div"
              page={page}
              count={modelliFiltrati.length}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPage={pageSize}
              rowsPerPageOptions={[10, 20, 50, 100]}
              onRowsPerPageChange={(e) => setPageSize(Number(e.target.value))}
              labelRowsPerPage={t("paginazione.righePerPagina")}
              labelDisplayedRows={({ from, to, count }) =>
                count === -1
                  ? t("paginazione.righeSenzaTotale", { from, to })
                  : t("paginazione.righe", { from, to, count })
              }
            />
          </Box>
        )}

        <ModelloDispositivoDialog
          open={modelliDialogState.open}
          modello={modelliDialogState.modello}
          onClose={() => {
            setModelliDialogState((x) => ({
              ...x,
              open: false,
            }));
          }}
        />
        {confirmationDialog}
      </Box>
      {/* Dialog */}
      <Dialog
        open={requestError !== null}
        onClose={() => setRequestError(null)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          <h2>{t("errori.titolo")}</h2>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {(requestError?.response?.data as string) ||
              t("errori.richiesta")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestError(null)} color="error">
            {t("azioni.ok")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default ModelliDispositivoPage;
